import { useEffect, useRef } from "react";

import {
  type MessageFragment,
  MessageRoleEnum,
  MessageStatusEnum,
} from "@app_schema";

import { track } from "@application/utilities/track";

type Answer = Pick<MessageFragment, "id" | "role" | "status">;

// Only a finished assistant reply is an answer: not the user's own message,
// and not a turn that is still running, failed or was stopped.
export const finishedAnswerId = (message?: Answer) => {
  if (message?.role !== MessageRoleEnum.Assistant) return;
  if (message.status !== MessageStatusEnum.Processed) return;

  return message.id;
};

// Tracks `assistant_answer_viewed` each time a conversation is opened and any of
// its newest answer is on screen in a visible tab; count answers seen by unique
// `message_id`. The assistant drawer stays mounted while closed, so rendered is not seen.
export const useAnswerViewed = ({
  conversationId,
  message,
}: {
  conversationId: string;
  message?: Answer;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const messageId = finishedAnswerId(message);

  useEffect(() => {
    const el = ref.current;
    if (!el || !messageId) return;

    let onScreen = false;
    let tracked = false;

    const report = () => {
      if (tracked || !onScreen) return;
      if (document.visibilityState !== "visible") return;

      tracked = true;
      track("assistant_answer_viewed", {
        conversation_id: conversationId,
        message_id: messageId,
      });
    };

    const observer = new IntersectionObserver(([entry]) => {
      onScreen = entry.isIntersecting;
      report();
    });
    observer.observe(el);
    document.addEventListener("visibilitychange", report);

    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", report);
    };
  }, [conversationId, messageId]);

  return ref;
};
